/**
 * Top Navigation Bar Component
 * Main navigation bar with logo, search, and user actions.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Bell, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthenticationContext';
import FeedbackModal from '../feedback/FeedbackModal';
import ThemeToggle from './ThemeToggle';
import './TopNavigationBar.css';

const TopNavigationBar = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Poll unread notification count while logged in. ponytail: 60s polling beats a websocket here.
  useEffect(() => {
    if (!token) {
      setUnreadCount(0);
      return;
    }
    let active = true;
    const fetchUnread = async () => {
      try {
        const res = await axios.get('/api/notifications');
        if (active) setUnreadCount(res.data.unread || 0);
      } catch (e) {
        /* ignore */
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => { active = false; clearInterval(id); };
  }, [token, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          {token ? (
            <form onSubmit={handleSearch} className="navbar-search">
              <Search size={18} className="navbar-search-icon" />
              <input
                type="search"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search WriteFlow"
              />
            </form>
          ) : (
            <div className="navbar-spacer"></div>
          )}

          <div className="navbar-links">
            <ThemeToggle />
            <button
              onClick={() => setIsFeedbackOpen(true)}
              className="feedback-btn"
              title="Send Feedback"
            >
              Feedback
            </button>
            {token ? (
              <>
                <Link to="/notifications" className="navbar-bell" title="Notifications">
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="navbar-bell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </Link>
                {user && (
                  <Link to={`/profile/${user.id}`} className="navbar-user-link">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.username} className="navbar-avatar" />
                    ) : (
                      <div className="navbar-avatar-placeholder">
                        {user.username?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="navbar-username">{user.username}</span>
                  </Link>
                )}
                <button onClick={handleLogout} className="logout-btn">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-login-nav">Login</Link>
                <Link to="/register" className="btn-register-nav">Register</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <FeedbackModal 
        isOpen={isFeedbackOpen} 
        onClose={() => setIsFeedbackOpen(false)} 
      />
    </>
  );
};

export default TopNavigationBar;
